import { useEffect, useState } from "react";
import { Button, Loading } from "components";
import { Parking } from "helpers";
import { useCompanyManagementContext } from "pages/CompanyManagement/utils/CompanyManagementContext";
import { ParkingTableDataOptions } from "./components";
import { useParkingManagementContext } from "pages/ParkingManagement/utils";
import { CreditCard } from "components/icons/CreditCard";
import { Cash } from "components/icons/Cash";
import { Camera } from "components/icons/Camera";
import { Wheelchair } from "components/icons/Wheelchair";
import { FireAlarm } from "components/icons/FireAlarm";
import { t } from "i18next";

interface ParkingTableDataProps {
  onFlag: (parking: Parking) => void;
  onEdit: (parking: Parking) => void;
  onAdd: (parking: Parking) => void;
}

export const ParkingTableData = ({ onFlag, onEdit, onAdd }: ParkingTableDataProps) => {
  const { parkings, isLoading: isLoadingParkings, isFetching } = useParkingManagementContext();
  const [isLoading, setIsLoading] = useState<boolean>(isLoadingParkings || isFetching);

  useEffect(() => {
    setIsLoading(isLoadingParkings || isFetching);
  }, [isLoadingParkings, isFetching]);

  return (
    <>
      {!!parkings && parkings.length > 0 ? (
        parkings.map((parking, index) => (
          <tr
            key={index}
            className={`h-[100px] border-y text-center ${!parking.is_configured ? "bg-gray-100 text-gray-500" : ""}`}
          >
            <td className="w-full text-center text-sm">{parking.unique_parking_id}</td>
            <td className="w-full text-center text-sm">{parking.translation.EN.name}</td>
            <td className="w-full text-center text-sm">{parking.company.company_name}</td>
            <td className="w-full text-center text-sm">{parking.area_type}</td>
            <td className="w-full text-center text-sm">{parking.municipality.municipality_name}</td>
            <td className="w-full text-center text-sm">
              {parking.is_configured ? (
                "Yes"
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <span>X</span>
                  <span
                    className="text-decoration-underline cursor-pointer text-sm font-bold leading-6 text-primary"
                    onClick={() => onAdd(parking)}
                  >
                    {t("parkingManagement.configure")}
                  </span>
                </div>
              )}
            </td>
            <td className="w-full text-center text-sm">{parking.total_parking_slots}</td>
            <td className="w-full text-center text-sm">
              {parking.payment_options?.length ? (
                <div className="flex items-center justify-center gap-2">
                  {parking.payment_options.map((option, index) => (
                    <span key={`payment-${option.id || index}`} className="inline-flex items-center">
                      {option.option_name === "Card" ? (
                        <CreditCard />
                      ) : option.option_name === "Cash" ? (
                        <Cash />
                      ) : (
                        option.option_name
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                "-"
              )}
            </td>
            <td className="w-full text-center text-sm">
              {parking.amenities?.length ? (
                <div className="flex items-center justify-center gap-3">
                  {parking.amenities.map((amenity) => {
                    switch (amenity.amenity) {
                      case "Fire alarm":
                        return <FireAlarm key="fire-alarm" className="" />;
                      case "Cameras":
                        return <Camera key="cameras" className="" />;
                      case "Wheelchair":
                        return <Wheelchair key="wheelchair" className="" />;
                      default:
                        return <span key={`amenity-${amenity.id}`}>{amenity.amenity}</span>;
                    }
                  })}
                </div>
              ) : (
                "-"
              )}
            </td>
            {parking.is_configured ? (
              <td className="sticky right-0 z-10 w-[200px] min-w-[200px] bg-white text-center text-sm">
                <ParkingTableDataOptions parking={parking} onFlag={onFlag} onEdit={onEdit} />
              </td>
            ) : (
              <td></td>
            )}
          </tr>
        ))
      ) : (
        <tr>
          <td className="text-center text-gray-500">{t("parkingManagement.noParkingsAvailable")}</td>
        </tr>
      )}

      {isLoading && (
        <tr>
          <td>
            <Loading absolute className="inset-0" />
          </td>
        </tr>
      )}
    </>
  );
};
